<script lang="ts">
	import type { Device } from 'src/types/Device';
	import VideoContainer from 'src/components/Camera/CameraPlayer/VideoContainer.svelte';
	import WebRTCPlayer from 'src/components/Camera/CameraPlayer/WebRTCPlayer.svelte';
	import { Button, Input, Modal, Text, toast } from '@Shelly/shelly-design-system/index';
	import { createEventDispatcher, onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	import type { CameraZone, CameraZoneType } from '../../types/camera-zones';
	import { DEFAULT_COORDINATES } from '../../helpers/canvas-helpers';
	import { capFirstChar, rgbToCss } from 'src/helpers/utilities';
	import ZonesCanvas from '../../CameraPlayer/ZonesCanvas.svelte';
	import { CAMERA_ZONES_COLORS } from '../../helpers/camera-zone-helpers';
	import { user_data } from 'src/stores';

	export let device: Device;
	export let zone_id: string;
	export let showModal: boolean;
	export let zoneType: CameraZoneType = 'motion';
	export let defaultColorIndex: number = 0;

	const defaultZone: CameraZone = {
		name: '',
		enable: true,
		type: zoneType,
		color: CAMERA_ZONES_COLORS[defaultColorIndex % CAMERA_ZONES_COLORS.length],
		coordinates: [...DEFAULT_COORDINATES],
	};

	export let zone_settings: CameraZone = defaultZone;

	let currentZone: CameraZone = { ...defaultZone };
	let isLoading: boolean = false;

	const dispatch = createEventDispatcher();

	const handleCloseModal = () => dispatch('close');

	const selectColor = (color: CameraZone['color']) => {
		currentZone.color = color;
	};

	const handleSave = () => {
		isLoading = true;
		dispatch('save', { zone_id, zone: currentZone });
	};

	export const onSaveError = (message?: string) => {
		isLoading = false;
		toast.open({
			type: 'danger',
			title: message || $format('error_saving_zone', { default: 'Error saving zone' }),
		});
	};

	$: isPrivacy = (zone_settings?.type ?? zoneType) === 'privacy';
	$: modalTitle = isPrivacy
		? $format('modal_title_add_edit_privacy_zone', { default: 'Privacy Zone' })
		: $format('modal_title_add_edit_detection_zone', { default: 'Detection Zone' });
	// A Support Access session may not open a stream, so the zone canvas keeps
	// the container's empty background instead of a live frame.
	$: canPlayStream = $user_data?.temporary !== true;

	onMount(() => {
		if (zone_settings) {
			currentZone = { ...defaultZone, ...zone_settings };
		}
	});
</script>

<Modal {showModal} title={modalTitle} backdropClose={false} on:close={handleCloseModal}>
	<svelte:fragment slot="content">
		<Text element="h2" class="mb-s">
			{capFirstChar(
				$format('placeholder_name', {
					values: { type: modalTitle },
				}),
			)}</Text
		>

		<Input
			id="input-name"
			aria-labelledby="input-name"
			icon="fas fa-shield-halved"
			label={$format('label_name', { default: 'Name' })}
			bind:value={currentZone.name}
		/>

		<div class="mt-m">
			<div class="width-full">
				<Text element="h2" class="mb-s">
					{$format('camera_zone_color', {
						default: 'Zone Color',
					})}</Text
				>
				<div class="color-picker">
					{#each CAMERA_ZONES_COLORS as color}
						<button
							class="swatch {currentZone.color?.join(',') === color?.join(',')
								? 'selected'
								: ''}"
							style="background-color: {rgbToCss(color, 1)}"
							on:click={() => selectColor(color)}
							aria-label={rgbToCss(color)}
						/>
					{/each}
				</div>
			</div>
		</div>
		<div class="zone-editor-container mt-m">
			<ZonesCanvas bind:zone={currentZone} {isPrivacy} disabled={isLoading}>
				<VideoContainer isZoomable={false}>
					{#if canPlayStream}
						<WebRTCPlayer deviceId={device.id} showControls={false} previewSize="medium" />
					{/if}
				</VideoContainer>
			</ZonesCanvas>
		</div>
	</svelte:fragment>

	<div slot="footer" class="d-flex justify-content-end">
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			variant="secondary"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={handleCloseModal}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			responsiveSize={{
				sm: 'm',
			}}
			loading={isLoading}
			disabled={isLoading}
			on:click={handleSave}
		>
			{$format('button_save', { default: 'Save' })}
		</Button>
	</div>
</Modal>

<style>
	.color-picker {
		display: flex;
		gap: 8px;
		align-items: center;
	}
	/* swatches */
	.swatch {
		width: 25px;
		height: 25px;
		border-radius: 50%;
		border: 2px solid transparent;
		position: relative;
		cursor: pointer;
		transition: transform 0.1s;
		opacity: 0.8;
	}
	.swatch:hover {
		transform: scale(1.1);
		opacity: 1;
	}

	/* selected state */
	.swatch.selected {
		box-shadow: inset 0 0 0 2px #fff;
		opacity: 1;
	}
	.swatch.selected::after {
		content: '✓';
		font-size: 14px;
		color: #fff;
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
	}
</style>
